/**
 *  Solicitudes (solicitud/*) → /api/v1/requests
 *
 *  Forma vieja: la fila de spWeb_SolicitudesGet / GetHistory (IdSolicitud,
 *  NoSolicitud, IdEstado, Estado, Numero, NombreProyecto, NombrePersona,
 *  Fecha1-3, FechaVisita, Img1-3, Vid1, ImgEvidencia1-3…). Las imágenes y el
 *  video ahora son URLs firmadas de v1 (duran 1 hora).
 *
 *  Fechas: la BD guarda la hora "de pared" de Cancún y tanto Koa como v1 la
 *  regresan etiquetada como Z. Para no mover las horas, lo que el portal
 *  captura (datetime-local, sin zona) se manda con esa misma hora y la
 *  etiqueta Z, igual que hacía dateToUTC en Koa.
 */
import api from '../apiV1';
import { fail, list } from './index';

const STATUS_DONE = 7; // Realizado (solo con RATING_FLOW=done en api-go)
const STATUS_COMPLETED = 9; // Finalizado

// wallZ convierte un valor del portal a RFC 3339 conservando la hora de pared.
function wallZ(v) {
   if (v === undefined || v === null || v === '') return null;
   if (v instanceof Date || (typeof v === 'object' && typeof v.format === 'function')) {
      // Date o moment: su hora local es la que capturó el usuario.
      const d = v instanceof Date ? v : v.toDate();
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}Z`;
   }
   const s = String(v).trim();
   // Ya trae zona (lo que regresó la API): se respeta tal cual.
   if (/(Z|[+-]\d{2}:?\d{2})$/.test(s)) return new Date(s).toISOString();
   if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00:00Z`;
   const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(:\d{2})?/);
   return m ? `${m[1]}T${m[2]}${m[3] || ':00'}Z` : null;
}

const str = (v) => (v === null || v === undefined ? '' : String(v));
const bool = (v) => v === true || v === 1 || v === '1' || v === 'true';

// toLegacy arma la fila vieja a partir de una solicitud de v1.
export function toLegacy(r) {
   const img = r.media || {};
   const images = img.images || [];
   const evidence = img.evidence || [];
   const dates = r.proposedDates || [];
   const rating = r.rating || {};
   return {
      IdSolicitud: r.id,
      NoSolicitud: str(r.folio),
      Fecha: r.createdAt || null,
      IdEstado: r.status && r.status.id,
      Estado: str(r.status && r.status.name),
      IdContrato: r.contractId,
      NumeroContrato: str(r.contractNumber),
      IdPersona: r.owner && r.owner.id,
      NombrePersona: str(r.owner && r.owner.name),
      IdUnidad: r.unit && r.unit.id,
      Numero: str(r.unit && r.unit.name),
      IdProyecto: r.project && r.project.id,
      NombreProyecto: str(r.project && r.project.name),
      IdArea: r.area && r.area.id,
      IdEquipo: r.equipment && r.equipment.id,
      IdProblema: r.problem && r.problem.id,
      Comentarios: str(r.description),
      Img1: images[0] || '',
      Img2: images[1] || '',
      Img3: images[2] || '',
      Vid1: img.video || '',
      ImgEvidencia1: evidence[0] || '',
      ImgEvidencia2: evidence[1] || '',
      ImgEvidencia3: evidence[2] || '',
      AplicaGarantia: r.warranty ? r.warranty.applies : null,
      ComentariosAplica: str(r.warranty && r.warranty.notes),
      FechaAplica: (r.warranty && r.warranty.reviewedAt) || null,
      Fecha1: dates[0] || null,
      Fecha2: dates[1] || null,
      Fecha3: dates[2] || null,
      FechaVisita: (r.visit && r.visit.scheduledAt) || null,
      // Abiertos lee ComentariosVisita; Historial lo recibía como Comentario.
      ComentariosVisita: str(r.visit && r.visit.notes),
      Comentario: str(r.visit && r.visit.notes),
      // Notas del admin (Abiertos) y comentario de la valoración (Historial).
      ComentariosReparacion: str(r.repairNotes),
      ComentarioReparacion: str(rating.comment || r.repairNotes),
      Reparado: rating.repaired === undefined ? null : rating.repaired,
      Valoracion: rating.score === undefined ? null : rating.score,
      // v1 no expone RespuestaRealizado; se deriva de la valoración.
      RespuestaRealizado: rating.repaired === null || rating.repaired === undefined ? null : String(rating.repaired),
      DiasProceso: r.processDays === undefined ? null : r.processDays,
      FechaFinaliza: r.completedAt || null,
      realizado: false,
      // Reporte rápido de la app: área, equipo y problema "Por clasificar".
      PorClasificar: !!r.needsClassification,
   };
}

// range: el portal manda {Desde, Hasta} en YYYY-MM-DD.
const range = (data) => {
   const q = {};
   if (data.Desde) q.from = String(data.Desde).slice(0, 10);
   if (data.Hasta) q.to = String(data.Hasta).slice(0, 10);
   return q;
};

async function listRequests(path, data) {
   const res = await api.get(path, range(data));
   if (!res.ok) return fail(res);
   return list((res.data || []).map(toLegacy));
}

// --- archivos ---

function dataURLToBlob(url) {
   const [head, b64] = url.split(',');
   const mime = (head.match(/^data:([^;]+)/) || [])[1] || 'image/jpeg';
   const bin = atob(b64 || '');
   const bytes = new Uint8Array(bin.length);
   for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
   return new Blob([bytes], { type: mime });
}

// toBlob acepta File/Blob o data URL. Las URLs http son evidencias ya
// guardadas: no se vuelven a subir.
function toBlob(item) {
   if (!item) return null;
   if (typeof Blob !== 'undefined' && item instanceof Blob) return item;
   if (typeof item === 'string' && item.startsWith('data:')) return dataURLToBlob(item);
   return null;
}

// evidenceFiles regresa solo las evidencias nuevas. v1 las guarda en los
// lugares libres (evidencia1-3) sin pisar las que ya existen.
async function evidenceFiles(files) {
   return (files || [])
      .map(toBlob)
      .filter(Boolean)
      .slice(0, 3)
      .map((b, i) => ['evidence', new File([b], `evidencia${i + 1}.jpg`, { type: b.type || 'image/jpeg' })]);
}

// --- revisión (PATCH /requests/{id}) ---

// reviewBody traduce el formulario viejo. Solo manda el estado si cambió:
// v1 no acepta volver a mandar estados como 2 (Aplica garantía).
function reviewBody(data, current) {
   const body = {};
   if (data.AplicaGarantia !== undefined && data.AplicaGarantia !== null && data.AplicaGarantia !== '') {
      body.warrantyApplies = bool(data.AplicaGarantia);
   }
   if (data.ComentariosAplica !== undefined) body.warrantyNotes = str(data.ComentariosAplica);
   if (data.ComentariosReparacion !== undefined && data.ComentariosReparacion !== null) {
      body.repairNotes = str(data.ComentariosReparacion);
   }
   if (data.ComentariosVisita !== undefined && data.ComentariosVisita !== null) {
      body.visitNotes = str(data.ComentariosVisita);
   }
   if ('Fecha1' in data || 'Fecha2' in data || 'Fecha3' in data) {
      body.proposedDates = [data.Fecha1, data.Fecha2, data.Fecha3].map(wallZ).filter(Boolean);
   }
   const status = Number(data.IdEstado);
   if (status && (!current || current.status.id !== status)) body.status = status;
   return body;
}

// sendReview manda la revisión con o sin evidencia (multipart con el JSON en
// "data").
function sendReview(id, body, files) {
   if (files.length) return api.upload('PATCH', `requests/${id}`, files, { data: body });
   return api.patch(`requests/${id}`, body);
}

async function review(data, files) {
   const id = data.IdSolicitud;
   const cur = await api.get(`requests/${id}`);
   if (!cur.ok) return fail(cur);
   const body = reviewBody(data, cur.data);
   const evidence = await evidenceFiles(files);

   let res;
   if (bool(data.realizado)) {
      // "Marcar realizado": con RATING_FLOW=done va a 7 (espera la
      // calificación del propietario); si el flag está apagado v1 rechaza el 7
      // antes de guardar nada y se finaliza en 9, como hacía Koa.
      res = await sendReview(id, { ...body, status: STATUS_DONE }, evidence);
      if (!res.ok && res.status === 400 && res.schema && res.schema.status) {
         res = await sendReview(id, { ...body, status: STATUS_COMPLETED }, evidence);
      }
   } else {
      res = await sendReview(id, body, evidence);
   }
   if (res.ok) return { response: true };
   if (res.status === 409) {
      return { error: true, message: res.message || 'La solicitud ya está finalizada y no se puede modificar.' };
   }
   return fail(res);
}

// fetchRequest trae una solicitud por id en la forma vieja (la usa la liga
// de las notificaciones, que puede apuntar fuera del rango de fechas).
export async function fetchRequest(id) {
   const res = await api.get(`requests/${id}`);
   return res.ok ? toLegacy(res.data) : null;
}

export default {
   'GET solicitud/get': (data) => listRequests('requests', data),
   'GET solicitud/get/history': (data) => listRequests('requests/history', data),

   'GET solicitud/get/dashboard': async () => {
      const res = await api.get('requests/dashboard');
      if (!res.ok) return fail(res);
      const d = res.data || {};
      const points = d.lastWeek || [];
      // Koa mandaba las etiquetas como DD/MM/YYYY.
      const label = (iso) => {
         const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
         return m ? `${m[3]}/${m[2]}/${m[1]}` : str(iso);
      };
      return {
         nuevas: (d.new || []).map(toLegacy),
         agendadas: (d.scheduled || []).map(toLegacy),
         aplicadas: d.warrantyApplied || 0,
         noAplicadas: d.warrantyDenied || 0,
         dataGraph: points.map((p) => p.count),
         labelGraph: points.map((p) => label(p.date)),
      };
   },

   'GET solicitud/get/visitas': async (data) => {
      const res = await api.get(`requests/${data.IdSolicitud}/visits`);
      if (!res.ok) return fail(res);
      return list((res.data || []).map((v) => ({ Responsable: str(v.technician), Fecha: v.date || null, Comentario: str(v.notes) })));
   },

   'POST solicitud/create/visita': async (data) => {
      const res = await api.post(`requests/${data.IdSolicitud}/visits`, {
         technician: str(data.Responsable),
         date: wallZ(data.Fecha),
         notes: str(data.Comentario),
      });
      return res.ok ? { response: true } : fail(res);
   },

   // Historial manda JSON; Abiertos y Dashboard mandan archivos de evidencia.
   'POST solicitud/update': (data) => review(data, []),
   'POSTFILE solicitud/update': (data, { files }) => review(data, files),

   // Alta desde el portal a nombre de un propietario. v1 la crea en 4; lo
   // demás que capturaba el formulario (garantía, fechas, estado) se aplica
   // enseguida con una revisión.
   'POSTFILE solicitud/create': async (data, { files }) => {
      const images = (await Promise.all((files || []).slice(0, 3).map(toBlob)))
         .filter(Boolean)
         .map((b, i) => ['images', new File([b], `imagen${i + 1}.jpg`, { type: b.type || 'image/jpeg' })]);
      const res = await api.upload('POST', 'requests', images, {
         ownerId: data.IdPersona,
         unitId: data.IdUnidad,
         areaId: data.IdArea,
         equipmentId: data.IdEquipo,
         problemId: data.IdProblema,
         description: str(data.Comentarios),
      });
      if (!res.ok) return fail(res);

      const id = res.data && res.data.id;
      const status = Number(data.IdEstado) || 4;
      const dates = [data.Fecha1, data.Fecha2, data.Fecha3].map(wallZ).filter(Boolean);
      const applies = bool(data.AplicaGarantia);
      if (id && (status !== 4 || applies || dates.length || str(data.ComentariosAplica))) {
         const body = { warrantyNotes: str(data.ComentariosAplica) };
         // Sin marcar la garantía no se manda false: v1 lo convertiría en 3
         // (No aplica), y Koa solo guardaba el 0 sin cambiar el estado.
         if (applies) body.warrantyApplies = true;
         else if (status === 3) body.warrantyApplies = false;
         if (dates.length) body.proposedDates = dates;
         if (status !== 4) body.status = status;
         const rev = await api.patch(`requests/${id}`, body);
         if (!rev.ok) {
            return { created: true, message: `La solicitud se creó, pero no se guardó la revisión: ${rev.message}` };
         }
      }
      return { created: true, message: res.message || 'ok' };
   },

   // Lo lee ServiceOrderDetails: {quantity, message}.
   'GET solicitud/:x/service-orders/count': async (data, { params }) => {
      const res = await api.get(`requests/${params[0]}/service-orders/count`);
      if (!res.ok) return fail(res);
      return res.data || { quantity: 0, message: '' };
   },
};
